import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import { GetInvoice } from "../actions/auth";
import { GenerateInvoicePDF } from "../components/pdf";
import moment from "moment";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CheckoutForm from "./checkoutForm";

const stripePromise = loadStripe("pk_test_51MUWGqG0xROCSBeSIzOmUcAVlgJgoSAj1JOmnoqkuX94fi2RMiujA0wdi9hCuWS13MXnjzCnvzS7VOy5nG1wG075009cn7krJB");

const PayInvoice = () => {
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState({
    invoiceNo: "",
    customerName: "",
    laborTotal: 0,
    partsTotal: 0,
    gogTotal: 0,
    servicesTotal: 0,
    parkingTotal: 0,
    loadbankTotal: 0,
    freightTotal: 0,
    shopFeeTotal: 0,
    subtotal: 0,
    tax: 0,
    total: 0,
  });
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    let inView = true;
    if (inView) {
      setLoading(true);
      // Fall back to lowercase for previously-sent email links (?invoiceid=...)
      const invoiceId = searchParams.get("invoiceId") ?? searchParams.get("invoiceid");
      GetInvoice({ invoiceId })
        .then((res) => {
          setInvoice(res.data);
          if (res.data.invoiceStatus === "paid") {
            setPaid(true);
            setStep(2);
          }
          setTimeout(() => {
            setLoading(false);
          }, 700);
        })
        .catch((err) => {
          toast.error(err.response.data.message);
          setTimeout(() => {
            navigate("/");
          }, 700);
        });
    }
    return () => {
      inView = false;
    };
  }, []);

  const currencyFormat = (value) => {
    let formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
    return formatted;
  };

  const displayLabor = () => (invoice.laborTotal ?? 0) + (invoice.servicesTotal ?? 0);
  const displayParts = () => (invoice.partsTotal ?? 0) + (invoice.gogTotal ?? 0);
  const displayMisc = () => (invoice.parkingTotal ?? 0) + (invoice.loadbankTotal ?? 0) + (invoice.freightTotal ?? 0) + (invoice.shopFeeTotal ?? 0);

  // Prior partial payments are subtracted server-side; fall back to total for safety
  const balanceDue = () => invoice.balanceDue ?? invoice.total ?? 0;

  const creditCardFee = () => {
    return parseFloat((balanceDue() * 0.03).toFixed(2));
  };

  const cardTotal = () => {
    return parseFloat((balanceDue() * 1.03).toFixed(2));
  };

  const printInvoice = () => {
    try {
      let data = {
        invoice: invoice,
        customer: invoice.company,
      };
      let doc = GenerateInvoicePDF(data, "none");
      doc.setProperties({
        title: `Invoice - ${invoice.invoiceNo}`,
        subject: `Invoice - ${invoice.invoiceNo}`,
        author: "Hypertek Solutions LLC",
        keywords: "",
        creator: "contact@hypertek.dev",
      });
      window.open(doc.output("bloburl"), "_blank");
    } catch (e) {
      console.error(e);
      toast.error("Unable to generate the invoice PDF");
    }
  };

  const nextStep = () => {
    // Refetch so the confirmation and printed PDF reflect the paid status
    GetInvoice({ invoiceId: invoice.invoiceId })
      .then((res) => {
        setInvoice(res.data);
      })
      .catch(() => {
        setInvoice((prev) => ({ ...prev, invoiceStatus: "paid" }));
      })
      .finally(() => {
        setPaid(true);
        setStep(2);
      });
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <div className="flex flex-col items-center justify-center w-full">
          <h1 className="text-2xl font-semibold uppercase font-body">Invoice {invoice.invoiceNo ?? ""}</h1>
          <p>Prepared for {invoice.customerName ?? ""}</p>
          <div className="w-full my-10 border-t border-gray-200" />
          <div className="flex flex-col items-center justify-between w-1/3 gap-1">
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Labor</p>
              <p>{currencyFormat(displayLabor())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Parts</p>
              <p>{currencyFormat(displayParts())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Misc. Shop</p>
              <p>{currencyFormat(displayMisc())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 pt-1 font-medium uppercase border-t border-gray-200 font-body">
              <p>Subtotal</p>
              <p>{currencyFormat(invoice.subtotal ?? 0)}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Tax</p>
              <p>{currencyFormat(invoice.tax ?? 0)}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 pt-1 font-medium uppercase border-t border-gray-200 font-body">
              <p>Total</p>
              <p>{currencyFormat(invoice.total ?? 0)}</p>
            </div>
            {(invoice.amountPaid ?? 0) > 0 && (
              <>
                <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body text-gray-600">
                  <p>Payments Received</p>
                  <p>-{currencyFormat(invoice.amountPaid)}</p>
                </div>
                <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
                  <p>Balance Due</p>
                  <p>{currencyFormat(balanceDue())}</p>
                </div>
              </>
            )}
            <div className="flex flex-row items-center justify-between w-full gap-1 pt-1 font-medium uppercase border-t border-gray-200 font-body text-gray-600">
              <p>Card Processing Fee (3%)</p>
              <p>{currencyFormat(creditCardFee())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Total When Paying By Card</p>
              <p>{currencyFormat(cardTotal())}</p>
            </div>
            <div className="flex flex-row items-center justify-center w-full gap-1 pt-2 uppercase font-body">
              <p className="text-[0.6rem] text-gray-600 text-center">A 3% processing fee will be applied if you choose to pay with a credit card.</p>
            </div>
          </div>
          <div className="w-full my-10 border-t border-gray-200" />
          <div className="flex flex-row items-center justify-end w-full">
            <button
              className="px-3 py-2 text-sm font-medium leading-4 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm font-body hover:bg-gray-50 focus:outline-none focus:ring-0 focus:ring-transparent disabled:hidden"
              onClick={() => setStep(1)}
              disabled={paid || balanceDue() <= 0}
            >
              Proceed To Payment
            </button>
          </div>
        </div>
      );
    } else if (step === 1) {
      return (
        <div className="flex flex-col items-center justify-center w-full">
          <h1 className="text-2xl font-semibold uppercase font-body">Invoice {invoice.invoiceNo ?? ""}</h1>
          <p className="pb-6 font-medium uppercase font-body">
            Amount to be charged: {currencyFormat(cardTotal())} <span className="text-xs text-gray-600 normal-case">(includes 3% card processing fee)</span>
          </p>
          <Elements stripe={stripePromise}>
            <CheckoutForm amount={getAmount()} invoiceId={invoice.invoiceId} nextStep={() => nextStep()} />
          </Elements>
        </div>
      );
    } else if (step === 2) {
      return (
        <div className="flex flex-col items-center justify-center w-full">
          <h1 className="text-2xl font-semibold uppercase font-body">Invoice {invoice.invoiceNo ?? ""} | PAID</h1>
          <p>Prepared for {invoice.customerName ?? ""}</p>
          <div className="w-full my-10 border-t border-gray-200" />
          <div className="flex flex-col items-center justify-between w-1/2 gap-1">
            <p className="text-xl text-center font-body">Thank you for your payment. We appreciate your business!</p>
            <p className="text-sm text-center font-body">You will receive a receipt containing a payment confirmation shortly.</p>
          </div>
          <div className="flex flex-col items-center justify-between w-1/3 gap-1 pt-5 mt-5 border-t border-gray-300">
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Labor</p>
              <p>{currencyFormat(displayLabor())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Parts</p>
              <p>{currencyFormat(displayParts())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Misc. Shop</p>
              <p>{currencyFormat(displayMisc())}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 pt-1 font-medium uppercase border-t border-gray-200 font-body">
              <p>Subtotal</p>
              <p>{currencyFormat(invoice.subtotal ?? 0)}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body">
              <p>Tax</p>
              <p>{currencyFormat(invoice.tax ?? 0)}</p>
            </div>
            <div className="flex flex-row items-center justify-between w-full gap-1 pt-1 font-medium uppercase border-t border-gray-200 font-body">
              <p>Total</p>
              <p>{currencyFormat(invoice.total ?? 0)}</p>
            </div>
            {(invoice.processingFees ?? 0) > 0 && (
              <>
                <div className="flex flex-row items-center justify-between w-full gap-1 font-medium uppercase font-body text-gray-600">
                  <p>Card Processing Fee (3%)</p>
                  <p>{currencyFormat(invoice.processingFees)}</p>
                </div>
                <div className="flex flex-row items-center justify-between w-full gap-1 pt-1 font-medium uppercase border-t border-gray-200 font-body">
                  <p>Total Paid</p>
                  <p>{currencyFormat(invoice.totalPaid ?? (invoice.total ?? 0) + invoice.processingFees)}</p>
                </div>
              </>
            )}
          </div>
          <div className="flex flex-row items-center justify-center w-full mt-8">
            <button
              className="px-4 py-2 text-sm font-bold text-white uppercase transition-all duration-150 ease-in-out bg-black border border-black font-body hover:bg-white hover:text-black"
              onClick={() => printInvoice()}
            >
              Print Invoice
            </button>
          </div>
        </div>
      );
    }
  };

  // Same math as the dashboard's card sale: charge the remaining balance plus a 3% processing fee.
  // The backend recomputes this server-side when creating the payment intent.
  const getAmount = () => {
    return Math.round(cardTotal() * 100);
  };

  return (
    <div className="flex flex-col items-center justify-start w-full h-full overflow-x-hidden overflow-y-auto">
      <div className="flex flex-row items-center justify-center w-full bg-black min-h-14 h-14">
        <div className="w-full max-w-[1200px] flex flex-row justify-between items-center text-white h-14">
          <p className="text-2xl cursor-pointer font-display">Hi-Tech Power Systems</p>
          <div className="flex flex-row items-center justify-end gap-4">
            <p className="uppercase font-semibold text-sm transition-all ease-in-out duration-150 hover:text-[#FFC312] cursor-pointer">Contact Us</p>
            <p
              className="uppercase font-semibold text-sm transition-all ease-in-out duration-150 hover:text-[#FFC312] cursor-pointer"
              onClick={() => navigate("/")}
            >
              Client Portal
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center my-8 w-full max-w-[1200px] mx-auto items-center gap-2 flex-1">{renderStep()}</div>
      <div className="flex flex-row items-center justify-center w-full border-t border-gray-300">
        <div className="h-14 w-full max-w-[1200px] mx-auto flex flex-row justify-between items-center text-xs py-3">
          <p>
            All rights reserved © Copyright 1986 - {moment().format("YYYY")} |{" "}
            <a href="https://hitechpowersystems.com" target="_blank" rel="noreferrer" className="font-medium">
              Hi-Tech Power Systems
            </a>
          </p>
          <p>
            Powered by{" "}
            <a
              href="https://liveuptothehype.com"
              target="_blank"
              rel="noreferrer"
              className="transition-all duration-150 ease-in-out hover:text-[#f20c2e] font-medium"
            >
              Hypertek Solutions LLC
            </a>{" "}
            | Live up to the hype!
          </p>
        </div>
      </div>
    </div>
  );
};

export default PayInvoice;
